import { useState } from "react";
import { motion } from "framer-motion";
import { Eye } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const FALLBACK_IMAGES = {
  "T-shirt": "https://images.pexels.com/photos/12025472/pexels-photo-12025472.jpeg",
  "Hoodie": "https://images.pexels.com/photos/8217473/pexels-photo-8217473.jpeg",
  "Sweatshirt": "https://images.pexels.com/photos/8217372/pexels-photo-8217372.jpeg",
  "Mug": "https://images.pexels.com/photos/11075707/pexels-photo-11075707.jpeg",
  "Cap": "https://images.pexels.com/photos/1124465/pexels-photo-1124465.jpeg",
  "Tote bag": "https://images.pexels.com/photos/9869067/pexels-photo-9869067.jpeg",
  "Zip hoodie": "https://images.pexels.com/photos/8217473/pexels-photo-8217473.jpeg",
  "Polo": "https://images.pexels.com/photos/12039633/pexels-photo-12039633.jpeg",
  "Socks": "https://images.pexels.com/photos/6787035/pexels-photo-6787035.jpeg",
};

const getImageUrl = (url, productName) => {
  if (!url) return FALLBACK_IMAGES[productName] || FALLBACK_IMAGES["T-shirt"];
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api")) return `${BACKEND_URL}${url}`;
  return url;
};

export default function ProductCard({ product, onClick, brandColors }) {
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const fallbackImage = FALLBACK_IMAGES[product.name] || FALLBACK_IMAGES["T-shirt"];
  const imageUrl = imageError 
    ? fallbackImage
    : getImageUrl(product.image_url, product.name);

  return (
    <motion.div
      className="product-card bg-white rounded-2xl overflow-hidden cursor-pointer border border-gray-100"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      whileHover={{ y: -4 }}
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-gray-50">
        <motion.img
          src={imageUrl}
          alt={product.name}
          className="w-full h-full object-cover product-image"
          animate={{ scale: isHovered ? 1.05 : 1 }}
          transition={{ duration: 0.3 }}
          onError={() => setImageError(true)}
        />
        
        {/* Hover Overlay */}
        <motion.div
          className="absolute inset-0 bg-black/40 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="bg-white rounded-full px-6 py-3 flex items-center gap-2 font-medium"
            initial={{ y: 10 }}
            animate={{ y: isHovered ? 0 : 10 }}
            transition={{ duration: 0.2 }}
          >
            <Eye className="w-4 h-4" />
            View Details
          </motion.div>
        </motion.div>

        {/* Category Badge */}
        <div className="absolute top-3 left-3">
          <span className="bg-white/90 backdrop-blur-sm text-xs font-medium px-3 py-1 rounded-full text-gray-700">
            {product.category}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-semibold text-gray-900 text-lg mb-1">
          {product.name}
        </h3>
        
        <div className="flex items-center justify-between">
          <span className="text-xl font-bold text-gray-900">
            {product.currency}{product.price}
          </span>
          
          {/* Color Variants */}
          <div className="flex items-center gap-1">
            {product.colors?.slice(0, 3).map((color, i) => (
              <div
                key={i}
                className="w-5 h-5 rounded-full border-2 border-white shadow-sm"
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
